import React, { useState } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, collectionGroup, limit } from 'firebase/firestore';

// IMPORTACIÓN DE UTILIDADES DE REHABILITACIÓN
import { rehabilitarPolizaFull, rehabilitarContratoFull } from '../../firebase/firebaseUtils';

/**
 * Utilidad para normalizar búsquedas numéricas (DNI, CUIT, Tel)
 * Elimina todo lo que no sea número.
 */
const normalizeNumeric = (val) => String(val || '').replace(/[^0-9]/g, '');

/**
 * Utilidad para normalizar patentes (quita espacios y guiones)
 */
const normalizePatente = (val) => String(val || '').toUpperCase().replace(/[-\s]/g, '');

/**
 * Extrae la parte fundamental de un teléfono para comparaciones elásticas.
 * Quita prefijos 54, 9, 0, 15.
 */
const getBasePhone = (num) => {
    let clean = normalizeNumeric(num);
    if (clean.startsWith('54')) clean = clean.substring(2);
    if (clean.startsWith('9')) clean = clean.substring(1);
    if (clean.startsWith('0')) clean = clean.substring(1);
    if (clean.startsWith('15')) clean = clean.substring(2);
    return clean;
};

const BuscadorInteligente = ({ scope = 'global', onResult, onLoading, placeholder, autoFocus = false }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSearch = async (e) => {
        if (e) e.preventDefault();
        const termRaw = searchTerm.trim();
        
        if (termRaw.length < 3) return; 

        setLoading(true);
        if (onLoading) onLoading(true);

        const termUpper = termRaw.toUpperCase();
        const termClean = normalizeNumeric(termRaw);
        const termPatente = normalizePatente(termRaw);
        const termLower = termRaw.toLowerCase();
        const searchBasePhone = getBasePhone(termRaw);

        // Versión base sin prefijos comunes para comparaciones elásticas (Teléfonos)
        let baseNum = termClean;
        if (baseNum.startsWith('54')) baseNum = baseNum.substring(2);
        if (baseNum.startsWith('0')) baseNum = baseNum.substring(1);
        if (baseNum.startsWith('15')) baseNum = baseNum.substring(2);

        const searchWords = termUpper.split(/\s+/).filter(w => w.length >= 2);
        const resultsMap = new Map();

        try {
            // -------------------------------------------------------------
            // ESTRATEGIA 1: BÚSQUEDA DIRECTA (N° Póliza, Contrato, Patente)
            // -------------------------------------------------------------
            if (scope === 'global' || scope === 'polizas') {
                const directQueries = [];
                directQueries.push(query(collectionGroup(db, 'polizas'), where('nPoliza', '==', termUpper), limit(20)));
                directQueries.push(query(collectionGroup(db, 'contratos'), where('nContrato', '==', termUpper), limit(20)));

                if (termPatente.length >= 3) {
                    directQueries.push(query(collectionGroup(db, 'polizas'), where('patente', '==', termUpper), limit(20)));
                    if (termPatente !== termUpper) {
                        directQueries.push(query(collectionGroup(db, 'polizas'), where('patente', '==', termPatente), limit(20)));
                    }
                    directQueries.push(query(collectionGroup(db, 'polizas'), where('patentes', 'array-contains', termUpper), limit(20)));
                    if (termPatente !== termUpper) {
                        directQueries.push(query(collectionGroup(db, 'polizas'), where('patentes', 'array-contains', termPatente), limit(20)));
                    }
                }

                const directResults = await Promise.allSettled(directQueries.map(q => getDocs(q)));

                for (const result of directResults) {
                    if (result.status === 'fulfilled') {
                        result.value.forEach(docSnap => {
                            if (!resultsMap.has(docSnap.id)) {
                                const data = docSnap.data();
                                const type = data.capitas !== undefined ? 'contrato' : 'poliza';
                                const parentDoc = docSnap.ref.parent?.parent;
                                const clienteId = parentDoc ? parentDoc.id : null;
                                if (clienteId) resultsMap.set(docSnap.id, { type, id: docSnap.id, clienteId, ...data });
                            }
                        });
                    }
                }
            }

            // -------------------------------------------------------------
            // ESTRATEGIA 2: BÚSQUEDA POR PERSONA (DNI, CUIT, Nombre, Tel, Email)
            // -------------------------------------------------------------
            let collectionsToSearch = [];
            if (scope === 'clientes' || scope === 'global' || scope === 'global_entidades') {
                collectionsToSearch.push({ ref: collection(db, 'public/data/clientes'), type: 'cliente' });
            }
            if (scope === 'prospectos' || scope === 'global' || scope === 'global_entidades') {
                collectionsToSearch.push({ ref: collection(db, 'public/data/prospectos'), type: 'prospecto' });
            }

            if (collectionsToSearch.length > 0) {
                const entityQueries = [];

                collectionsToSearch.forEach(({ ref, type }) => {
                    // 1. DNI / CUIT
                    if (termClean.length >= 7) {
                        entityQueries.push({ query: query(ref, where("dni", "==", termClean)), type });
                        entityQueries.push({ query: query(ref, where("cuit", "==", termClean)), type });
                    }

                    // 2. TELÉFONOS (Coexistencia y Arreglos con variantes para evitar rigidez de Firestore)
                    if (termClean.length >= 3) {
                        // Variantes para "engañar" al array-contains exacto
                        const phoneVariants = new Set([
                            termClean,
                            '549' + searchBasePhone,
                            '9' + searchBasePhone,
                            '0' + searchBasePhone,
                            searchBasePhone
                        ]);

                        phoneVariants.forEach(v => {
                            entityQueries.push({ query: query(ref, where("telefonos", "array-contains", v)), type });
                        });

                        // Búsqueda por prefijo en campo plano legacy
                        entityQueries.push({ query: query(ref, where("telefono", ">=", termClean.substring(0, 4)), where("telefono", "<=", termClean.substring(0, 4) + '\uf8ff'), limit(50)), type });
                    }

                    // 3. EMAILS (Coexistencia y Arreglos)
                    if (termLower.includes('@') || termLower.length >= 5) {
                        entityQueries.push({ query: query(ref, where("email", "==", termLower)), type });
                        entityQueries.push({ query: query(ref, where("emails", "array-contains", termLower)), type });
                    }

                    // 4. Nombre / Apellido (Prefijo para candidatos)
                    if (searchWords.length > 0) {
                        const firstWord = searchWords[0];
                        entityQueries.push({ query: query(ref, where("apellido", ">=", firstWord), where("apellido", "<=", firstWord + '\uf8ff'), limit(50)), type });
                        entityQueries.push({ query: query(ref, where("nombre", ">=", firstWord), where("nombre", "<=", firstWord + '\uf8ff'), limit(50)), type });
                    }
                });
                
                const entityResults = await Promise.allSettled(entityQueries.map(item => 
                    getDocs(item.query).then(snap => ({ snap, type: item.type }))
                ));

                const peopleFound = new Map();

                entityResults.forEach(result => {
                    if(result.status === 'fulfilled') {
                        const { snap, type } = result.value;
                        snap.forEach(docSnap => {
                            const data = docSnap.data();
                            const fullName = `${data.nombre || ''} ${data.apellido || ''} ${data.nombreCompleto || ''}`.toUpperCase();
                            
                            // --- FILTRO 1: LÓGICA AND PARA NOMBRES ---
                            const matchesName = searchWords.length > 0 && searchWords.every(word => fullName.includes(word));

                            // --- FILTRO 2: TELÉFONO ELÁSTICO (Comprobación final en memoria) ---
                            const allPhones = [
                                ...(Array.isArray(data.telefonos) ? data.telefonos : []),
                                data.telefono
                            ].filter(Boolean);
                            
                            const matchesPhone = searchBasePhone.length >= 6 && allPhones.some(t => {
                                const cleanT = getBasePhone(t);
                                return cleanT.includes(searchBasePhone) || searchBasePhone.includes(cleanT);
                            });

                            const matchesId = termClean.length >= 7 && (normalizeNumeric(data.dni) === termClean || normalizeNumeric(data.cuit) === termClean);
                            
                            const allEmails = [
                                ...(Array.isArray(data.emails) ? data.emails : []),
                                data.email
                            ].filter(Boolean);
                            const matchesEmail = termLower.includes('@') && allEmails.some(e => String(e).toLowerCase() === termLower);

                            if (matchesName || matchesPhone || matchesId || matchesEmail) {
                                peopleFound.set(docSnap.id, { id: docSnap.id, type, ...data });
                            }
                        });
                    }
                });

                // --- PASO 3: PROCESAR EN CASCADA ---
                if (peopleFound.size > 0) {
                    const peopleArray = Array.from(peopleFound.values());

                    if (scope === 'global') {
                        const subQueries = [];
                        peopleArray.forEach(person => {
                            const clientRefPath = `public/data/clientes/${person.id}`;
                            subQueries.push(getDocs(collection(db, clientRefPath, 'polizas')).then(s => ({ snap: s, type: 'poliza', clientId: person.id })));
                            subQueries.push(getDocs(collection(db, clientRefPath, 'contratos')).then(s => ({ snap: s, type: 'contrato', clientId: person.id })));
                        });

                        const subResults = await Promise.allSettled(subQueries);
                        let foundAnySubItem = false;

                        subResults.forEach(res => {
                            if (res.status === 'fulfilled') {
                                const { snap, type, clientId } = res.value;
                                snap.forEach(pDoc => {
                                    if (!resultsMap.has(pDoc.id)) {
                                        resultsMap.set(pDoc.id, { type, id: pDoc.id, clienteId: clientId, ...pDoc.data() });
                                        foundAnySubItem = true;
                                    }
                                });
                            }
                        });

                        if (!foundAnySubItem) {
                            peopleArray.forEach(p => resultsMap.set(p.id, p));
                        }
                    } else {
                        peopleArray.forEach(p => { if (!resultsMap.has(p.id)) resultsMap.set(p.id, p); });
                    }
                }
            }

            const finalResults = Array.from(resultsMap.values());
            
            // Ordenamiento por relevancia
            finalResults.sort((a, b) => {
                const priority = { 'poliza': 1, 'contrato': 1, 'cliente': 2, 'prospecto': 3 };
                return (priority[a.type] || 99) - (priority[b.type] || 99);
            });

            onResult(finalResults);

        } catch (error) {
            console.error("Error crítico en BuscadorInteligente:", error);
            onResult([]);
        } finally {
            setLoading(false);
            if (onLoading) onLoading(false);
        }
    };

    return (
        <form onSubmit={handleSearch} className="w-full relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg className={`w-5 h-5 ${loading ? 'text-blue-500 animate-pulse' : 'text-gray-400 group-focus-within:text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
            </div>
            <input 
                type="text" 
                className="w-full bg-white border border-gray-200 rounded-xl py-3.5 pl-12 pr-32 text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm uppercase"
                placeholder={placeholder || "Buscar por Cliente, DNI, Poliza o Patente..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus={autoFocus}
            />
            <button type="submit" disabled={loading} className="absolute right-2 top-2 bottom-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 rounded-lg transition-all shadow-sm disabled:opacity-70 text-sm">
                {loading ? '...' : 'Buscar'}
            </button>
        </form>
    );
};

export default BuscadorInteligente;